/**
 * C7's procurement routes (design §5.5).
 *
 * A separate file from `routes.ts`, spread into `PROCUREMENT_ROUTES` there, for
 * the same reason `ap/routesC3.ts` and `fx/routesC1.ts` exist: a milestone's
 * additions are reviewable as a block and a merge conflict in one milestone's
 * routes cannot silently drop another's. The router imports one array and does
 * not know or care about the split.
 *
 * Every `:id` segment carries the `\d+` constraint, so no literal word can be
 * parsed as a document id — `/procurement/supplier-returns/new` would otherwise
 * resolve to the detail screen for return "new".
 *
 * **Which permission guards which screen follows the API, not the topic.** The
 * receipt inspection screen is `procurement.grn.read` because that is what
 * `GET /goods-receipts/:id/inspection` declares; *releasing* a quarantined line
 * needs `procurement.grn.inspect`, and the screen checks that separately. The
 * supplier 360 is `procurement.supplier.read` rather than `ap.vendor.read`,
 * because the latter gates the vendor's encrypted bank details and a buyer
 * reading a qualification has no business seeing those.
 *
 * The match-exception inbox is **not** the approvals inbox and is deliberately
 * its own route: an exception has an owner, not an approver, and folding it in
 * would make "approve" mean "I have investigated this".
 */

import type { RouteRecordRaw } from 'vue-router'

import { PROCUREMENT_PERMISSIONS } from '@/modules/procurement/permissions'

export const PROCUREMENT_C7_ROUTES: RouteRecordRaw[] = [
  /* ------------------------------------------- receiving, quarantine, returns */
  {
    path: '/procurement/receipts/:id(\\d+)/inspection',
    name: 'procurement-grn-inspection',
    component: () => import('@/modules/procurement/receiving/GrnInspectionView.vue'),
    meta: {
      titleKey: 'procurement.c7.routeTitles.grnInspection',
      permission: PROCUREMENT_PERMISSIONS.grnRead,
    },
  },
  {
    path: '/procurement/supplier-returns',
    name: 'procurement-supplier-returns',
    component: () => import('@/modules/procurement/receiving/SupplierReturnsListView.vue'),
    meta: {
      titleKey: 'procurement.c7.routeTitles.returns',
      permission: PROCUREMENT_PERMISSIONS.grnRead,
    },
  },
  {
    path: '/procurement/supplier-returns/:id(\\d+)',
    name: 'procurement-supplier-return',
    component: () => import('@/modules/procurement/receiving/SupplierReturnView.vue'),
    meta: {
      titleKey: 'procurement.c7.routeTitles.return',
      permission: PROCUREMENT_PERMISSIONS.grnRead,
    },
  },

  /* ------------------------------------------------------- supplier lifecycle */
  {
    path: '/procurement/suppliers',
    name: 'procurement-suppliers',
    component: () => import('@/modules/procurement/suppliers/SuppliersListView.vue'),
    meta: {
      titleKey: 'procurement.c7.routeTitles.suppliers',
      permission: PROCUREMENT_PERMISSIONS.supplierRead,
    },
  },
  {
    path: '/procurement/suppliers/:vendorId(\\d+)',
    name: 'procurement-supplier',
    component: () => import('@/modules/procurement/suppliers/SupplierView.vue'),
    meta: {
      titleKey: 'procurement.c7.routeTitles.supplier',
      permission: PROCUREMENT_PERMISSIONS.supplierRead,
    },
  },
  {
    path: '/procurement/supplier-policies',
    name: 'procurement-supplier-policies',
    component: () => import('@/modules/procurement/suppliers/SupplierPolicyView.vue'),
    meta: {
      titleKey: 'procurement.c7.routeTitles.supplierPolicy',
      permission: PROCUREMENT_PERMISSIONS.settingsManage,
    },
  },
  {
    path: '/procurement/reports/supplier-risk',
    name: 'procurement-supplier-risk',
    component: () => import('@/modules/procurement/suppliers/SupplierRiskReportView.vue'),
    meta: {
      titleKey: 'procurement.c7.routeTitles.supplierRisk',
      permission: PROCUREMENT_PERMISSIONS.reportRead,
    },
  },

  /* --------------------------------------------- contracts and blanket orders */
  {
    path: '/procurement/contracts',
    name: 'procurement-contracts',
    component: () => import('@/modules/procurement/contracts/ContractsListView.vue'),
    meta: {
      titleKey: 'procurement.c7.routeTitles.contracts',
      permission: PROCUREMENT_PERMISSIONS.contractRead,
    },
  },
  {
    path: '/procurement/contracts/:id(\\d+)',
    name: 'procurement-contract',
    component: () => import('@/modules/procurement/contracts/ContractView.vue'),
    meta: {
      titleKey: 'procurement.c7.routeTitles.contract',
      permission: PROCUREMENT_PERMISSIONS.contractRead,
    },
  },
  {
    path: '/procurement/contracts/:id(\\d+)/releases',
    name: 'procurement-contract-releases',
    component: () => import('@/modules/procurement/contracts/ReleaseBoardView.vue'),
    meta: {
      titleKey: 'procurement.c7.routeTitles.releases',
      permission: PROCUREMENT_PERMISSIONS.contractRead,
    },
  },
  {
    path: '/procurement/reports/contract-expiry',
    name: 'procurement-contract-expiry',
    component: () => import('@/modules/procurement/contracts/ContractExpiryView.vue'),
    meta: {
      titleKey: 'procurement.c7.routeTitles.contractExpiry',
      permission: PROCUREMENT_PERMISSIONS.contractRead,
    },
  },

  /* ------------------------------------------------------- spend and maverick */
  {
    path: '/procurement/spend-analysis',
    name: 'procurement-spend-analysis',
    component: () => import('@/modules/procurement/spend/SpendAnalysisView.vue'),
    meta: {
      titleKey: 'procurement.c7.routeTitles.spendAnalysis',
      permission: PROCUREMENT_PERMISSIONS.spendRead,
    },
  },
  {
    path: '/procurement/spend-analysis/maverick',
    name: 'procurement-maverick',
    component: () => import('@/modules/procurement/spend/MaverickReportView.vue'),
    meta: {
      titleKey: 'procurement.c7.routeTitles.maverick',
      permission: PROCUREMENT_PERMISSIONS.spendRead,
    },
  },
  {
    path: '/procurement/spend-facts',
    name: 'procurement-spend-facts',
    component: () => import('@/modules/procurement/spend/SpendFactsView.vue'),
    meta: {
      titleKey: 'procurement.c7.routeTitles.spendFacts',
      permission: PROCUREMENT_PERMISSIONS.spendRead,
    },
  },

  /* -------------------------------------------------- the match exception inbox */
  {
    path: '/procurement/match-exceptions',
    name: 'procurement-match-exceptions',
    component: () => import('@/modules/procurement/matching/MatchExceptionsInboxView.vue'),
    meta: {
      titleKey: 'procurement.c7.routeTitles.matchExceptions',
      permission: PROCUREMENT_PERMISSIONS.matchRead,
    },
  },
  {
    path: '/procurement/match-causes',
    name: 'procurement-match-causes',
    component: () => import('@/modules/procurement/matching/MatchCausesView.vue'),
    meta: {
      titleKey: 'procurement.c7.routeTitles.matchCauses',
      permission: PROCUREMENT_PERMISSIONS.matchRead,
    },
  },

  /* ----------------------------------------------------------------- PO depth */
  {
    path: '/procurement/orders/:id(\\d+)/amendments',
    name: 'procurement-order-amendments',
    component: () => import('@/modules/procurement/orders/AmendmentsView.vue'),
    meta: {
      titleKey: 'procurement.c7.routeTitles.amendments',
      permission: PROCUREMENT_PERMISSIONS.poRead,
    },
  },
  {
    path: '/procurement/orders/:id(\\d+)/delivery-schedule',
    name: 'procurement-order-delivery-schedule',
    component: () => import('@/modules/procurement/orders/DeliveryScheduleView.vue'),
    meta: {
      titleKey: 'procurement.c7.routeTitles.deliverySchedule',
      permission: PROCUREMENT_PERMISSIONS.poRead,
    },
  },
  {
    path: '/procurement/incoterms',
    name: 'procurement-incoterms',
    component: () => import('@/modules/procurement/orders/IncotermsView.vue'),
    meta: {
      titleKey: 'procurement.c7.routeTitles.incoterms',
      permission: PROCUREMENT_PERMISSIONS.poRead,
    },
  },

  /* ---------------------------------------------------------------- RFQ depth */
  {
    path: '/procurement/rfqs/:id(\\d+)/evaluation',
    name: 'procurement-rfq-evaluation',
    component: () => import('@/modules/procurement/rfq/RfqEvaluationView.vue'),
    meta: {
      titleKey: 'procurement.c7.routeTitles.rfqEvaluation',
      permission: PROCUREMENT_PERMISSIONS.rfqRead,
    },
  },
]

/** The same set as strings, for links inside the module. */
export const PROCUREMENT_C7_PATHS = {
  grnInspection: (grnId: number) => `/procurement/receipts/${grnId}/inspection`,
  returns: '/procurement/supplier-returns',
  supplierReturn: (id: number) => `/procurement/supplier-returns/${id}`,
  suppliers: '/procurement/suppliers',
  supplier: (vendorId: number) => `/procurement/suppliers/${vendorId}`,
  supplierPolicy: '/procurement/supplier-policies',
  supplierRisk: '/procurement/reports/supplier-risk',
  contracts: '/procurement/contracts',
  contract: (id: number) => `/procurement/contracts/${id}`,
  contractReleases: (id: number) => `/procurement/contracts/${id}/releases`,
  contractExpiry: '/procurement/reports/contract-expiry',
  spendAnalysis: '/procurement/spend-analysis',
  maverick: '/procurement/spend-analysis/maverick',
  spendFacts: '/procurement/spend-facts',
  matchExceptions: '/procurement/match-exceptions',
  matchCauses: '/procurement/match-causes',
  amendments: (poId: number) => `/procurement/orders/${poId}/amendments`,
  deliverySchedule: (poId: number) => `/procurement/orders/${poId}/delivery-schedule`,
  incoterms: '/procurement/incoterms',
  rfqEvaluation: (rfqId: number) => `/procurement/rfqs/${rfqId}/evaluation`,
} as const
